import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  OrderType,
  OrderStatus,
  DriverType,
  CustomFieldItem,
  OrderDropoffAddress,
  OrderDeliveryAddress,
  Address,
  OrderEvent,
  OrderCustomFields,
  OrderCustomFieldDifinitions,
} from "@app/types/orders";
import { fetchTrackOrder } from "./sharedThunks";
import { ApiError } from "@app/types/api";
import { t } from '@app/utils/translation'


export interface OrderState {
  orderFetchStatus: "idle" | "loading" | "succeeded" | "failed";
  error: ApiError | null;
  orderId: string;
  code: string;
  trackOrderCode: string;
  type?: typeof OrderType[number];
  status: OrderStatus;
  statusDescription: string;
  driver?: DriverType;
  events: {
    // title: string;
    // description: string;
    orderStatusDescription: string
    createdAt: number;
    /** Only populated from the initial track response (see
     * `fetchTrackOrder.fulfilled`) — the `getEvents` refetch endpoint
     * (`EventUpdater`) doesn't return these, so they're optional rather
     * than synthesized here. */
    id?: string;
    source?: OrderEvent['source'];
  }[],
  isEventNeedUpdate: boolean;
  realETA: number;
  stage?: "Pickup" | "Delivery";
  customFields: CustomFieldItem[] | null;
  /** Raw order-level custom-field values/definitions, kept alongside the
   * already-resolved `customFields` above (used by legacy UI). New (v2)
   * consumers read these instead — see `Address.rawCustomFields` for why. */
  rawCustomFields: OrderCustomFields | null;
  rawCustomFieldDefinitions: OrderCustomFieldDifinitions[] | null;
  pickup?: Address;
  dropoffs?: Address[];
  remainingQueuePosition?: number;
  isLastDropOff?: boolean;
  isDropoffTracking: boolean;
}

const initialState: OrderState = {
  orderFetchStatus: 'idle',
  error: null,
  orderId: "",
  code: "",
  trackOrderCode: "",
  type: undefined,
  status: "Assigned",
  statusDescription: "",
  events: [],
  isEventNeedUpdate: false,
  realETA: 0,
  customFields: null,
  rawCustomFields: null,
  rawCustomFieldDefinitions: null,
  isDropoffTracking: false
};

export const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    clearOrder: (state) => {
      state.orderFetchStatus = initialState.orderFetchStatus;
      state.error = initialState.error;
      state.orderId = initialState.orderId;
      state.code = initialState.code;
      state.trackOrderCode = initialState.trackOrderCode;
      state.type = initialState.type;
      state.status = initialState.status;
      state.statusDescription = initialState.statusDescription;
      state.events = initialState.events;
      state.isEventNeedUpdate = initialState.isEventNeedUpdate;
      state.realETA = initialState.realETA;
      state.stage = initialState.stage;
      state.remainingQueuePosition = initialState.remainingQueuePosition;
      state.isLastDropOff = initialState.isLastDropOff;
      state.isDropoffTracking = initialState.isDropoffTracking;
      state.rawCustomFields = initialState.rawCustomFields;
      state.rawCustomFieldDefinitions = initialState.rawCustomFieldDefinitions;

    },
    setOrderId: (state, action: PayloadAction<OrderState["orderId"]>) => {
      state.orderId = action.payload;
    },
    setOrderCode: (state, action: PayloadAction<OrderState["code"]>) => {
      state.code = action.payload;
    },
    setOrderType: (state, action: PayloadAction<OrderState["type"]>) => {
      state.type = action.payload;
    },
    setOrderStatus: (
      state,
      action: PayloadAction<Pick<OrderState, "status" | "statusDescription">>
    ) => {
      if((state.status != action.payload.status) && state.type !== 'Ondemand' ) state.isEventNeedUpdate = true;
      state.status = action.payload.status;
      state.statusDescription = action.payload.statusDescription;

    },
    setOrderDriver: (state, action: PayloadAction<DriverType>) => {
      if (!state.driver) state.driver = Object();
      if (state.driver) {
        state.driver.avatarPhoto = action.payload.avatarPhoto;
        state.driver.name = action.payload.name;
        state.driver.phone = action.payload.phone;
        state.driver.rate = action.payload.rate;
        state.driver.vehicle = action.payload.vehicle;
        state.driver.vehicle.plate = action.payload.vehicle.plate;
        state.driver.vehicle.model = action.payload.vehicle.model;
        state.driver.vehicle.color = action.payload.vehicle.color;
        state.driver.customFields = action.payload.customFields;
        state.driver.customFieldDefinitions = action.payload.customFieldDefinitions;
      }
    },
    setOrderEvents: (state, action: PayloadAction<OrderState["events"]>) => {
      state.events = action.payload;
    },
    addOrderEvent: (
      state,
      action: PayloadAction<OrderState["events"][number]>
    ) => {
      state.events.push(action.payload);
    },
    setEventNeedUpdate: (state, action: PayloadAction<OrderState["isEventNeedUpdate"]>) => {
      state.isEventNeedUpdate = action.payload;
    },
    setRealETA: (state, action: PayloadAction<OrderState["realETA"]>) => {
      state.realETA = action.payload;
    },
    setStage: (state, action: PayloadAction<OrderState["stage"]>) => {
      state.stage = action.payload;
    },
    setOrderPickup : (state, action: PayloadAction<Address>) => {
      state.pickup = action.payload;
    },
    setOrderDropoffs: (state, action: PayloadAction<Address[]>) => {
      state.dropoffs = action.payload;
    },
    setRemainingQueuePosition: (state, action: PayloadAction<number>) => {
      state.remainingQueuePosition = action.payload;
    },
    setIsLastDropOff: (state, action: PayloadAction<boolean>) => {
      state.isLastDropOff = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackOrder.pending, (state) => {
        state.orderFetchStatus = "loading";
      })
      .addCase(fetchTrackOrder.fulfilled, (state, action) => {
        state.orderFetchStatus = "succeeded";
        let order = action.payload.order;
        let language = action.payload.language || 'en'
        
        state.orderId = order.id;
        state.code = order.code;
        state.trackOrderCode = action.meta.arg.dropoffTracking && action.meta.arg.trackCode ? action.meta.arg.trackCode : order.trackOrderCode;
        state.type = order.type;
        state.remainingQueuePosition = order.remainingQueuePosition;
        state.isLastDropOff = order.isLastDropOff;
        state.isDropoffTracking = !!action.meta.arg.dropoffTracking;

        if((state.status != order.status) && state.type !== 'Ondemand' ) state.isEventNeedUpdate = true;
        state.status = order.status;
        state.statusDescription = order.statusDescription[action.payload.language];

        if(order.driver){

          if (!state.driver) state.driver = Object();
          if (state.driver) {
            state.driver.avatarPhoto = order.driver.avatarPhoto;
            state.driver.name = order.driver.name;
            state.driver.phone = order.driver.phone;
            state.driver.rate = order.driver.rate;
            state.driver.vehicle = order.driver.vehicle;
            state.driver.customFields = order.driver.customFields;
            state.driver.customFieldDefinitions = order.driver.customFieldDefinitions;
          }

        }


        if (
          order.type !== "Ondemand" &&
          action.payload.events &&
          action.payload.events.length > 0
        ) {
          const events = action.payload.events.map((event) => ({
            // title: event.title[action.payload.language],
            // description: event.description[action.payload.language],
            orderStatusDescription: event.orderStatusDescription && event.orderStatusDescription[action.payload.language] || '-',
            createdAt: event.createdAt,
            id: event.id,
            source: event.source,
          }));


          state.events = events;
        }

        state.realETA = order.realETA;
        state.stage = order.stage;

        state.rawCustomFields = order.customFields;
        state.rawCustomFieldDefinitions = order.customFieldDefinitions;


        if(order.customFieldDefinitions && order.customFieldDefinitions.length > 0 && order.customFields){
          const orderCustomFields = order.customFields;           
          const customFields : OrderState['customFields'] = [];
          
          order.customFieldDefinitions.forEach(cfd => {
              if(orderCustomFields[cfd.key] !== undefined && orderCustomFields[cfd.key] !== "" && orderCustomFields[cfd.key] !== null){
                let value = orderCustomFields[cfd.key];
                let label = cfd.label;
                if(cfd.config.type === 'select'){
                    value = cfd.config.choices.find(c => c.key === orderCustomFields[cfd.key])?.label ?? '-';
                }else if (cfd.config.type === 'file'){
                  // let data: any = orderCustomFields[cfd.key];
                  // label = data.name;
                  // value = data.url;
                }else if (cfd.config.type === 'boolean' && orderCustomFields[cfd.key] === true){
                  let data: any = orderCustomFields[cfd.key];
                  value = data;
                }
                
                customFields.push({
                  key: cfd.key,
                  label: label,
                  value: value,
                  type: cfd.config.type
                })

              }
          });
          
          state.customFields = customFields;

        }
  

        if(order.pickup){

          let customFields : CustomFieldItem[] = [];
          if(order.pickup.customFieldDefinitions && order.pickup.customFieldDefinitions.length > 0 && order.pickup.customFields){
            const orderPickupCustomFields = order.pickup.customFields; 
  
            order.pickup.customFieldDefinitions.forEach(cfd => {
                if(orderPickupCustomFields[cfd.key] !== undefined && orderPickupCustomFields[cfd.key] !== "" && orderPickupCustomFields[cfd.key] !== null){
                  let value = orderPickupCustomFields[cfd.key];
                  let label = cfd.label;
                  if(cfd.config.type === 'select'){
                      value = cfd.config.choices.find(c => c.key === orderPickupCustomFields[cfd.key])?.label ?? '-';
                  }else if (cfd.config.type === 'file'){
                    let data: any = orderPickupCustomFields[cfd.key];
                    label = data.name;
                    value = data.url;
                  }else if (cfd.config.type === 'boolean' && orderPickupCustomFields[cfd.key] === true){
                    let data: any = orderPickupCustomFields[cfd.key];
                    value = data;
                  }
                  
                  customFields.push({
                    key: cfd.key,
                    label: label,
                    value: value,
                    type: cfd.config.type
                  })
  
                }
            });
            
            state.customFields = customFields;
  
          }

          let addressDetail = "";
          if(order.type === 'PickupDelivery'){
            addressDetail = order.pickup.addressDetail;
          }else {
            let block = order.pickup.buildingBlock ? `${t('Block' , language)} ${order.pickup.buildingBlock}, ` : '';
            let floor = order.pickup.floor ? `${t('Floor' , language)} ${order.pickup.floor}, `  : '';
            let room = order.pickup.room ? `${t('Room' , language)} ${order.pickup.room}`  : '';
            addressDetail = block + floor + room
          }
          
          state.pickup = {
            // The real API's pickup object never carries an `id` (only
            // Ondemand's `dropoffs[]` entries do) — fall back to a stable
            // sentinel so downstream id-based lookups (StopDetailsPage) can
            // still resolve it. Same reasoning as `driver.id` below.
            id: order.pickup.id || "pickup",
            lat: order.pickup.coordinates[1],
            lng: order.pickup.coordinates[0],
            address: order.pickup.address,
            addressDetail: addressDetail,
            customFields: customFields && customFields.length > 0 ? customFields : null,
            deliveryDate: order.pickup.deliveryDate,
            proofs: order.pickup.proofs,
            customerDescription: order.pickup.customerDescription,
            proofNote: order.pickup.popNote || order.pickup.driverDescription,
            rawCustomFields: order.pickup.customFields,
            rawCustomFieldDefinitions: order.pickup.customFieldDefinitions,
            contactName: order.pickup.fullName,
            contactPhone: order.pickup.phone,
          }
        }


        let tempDropoffs : (OrderDropoffAddress | OrderDeliveryAddress)[] = [];

        if(order.dropoffs && order.dropoffs.length > 0) tempDropoffs = order.dropoffs;
        if(order.dropoff) tempDropoffs.push(order.dropoff);
        if(order.delivery) tempDropoffs.push(order.delivery);



        if(tempDropoffs && tempDropoffs.length > 0){

          state.dropoffs = tempDropoffs.map((drop, index) =>{

            let customFields : CustomFieldItem[] = [];

            if(drop.customFieldDefinitions && drop.customFieldDefinitions.length > 0 && drop.customFields){
              const dropCustomFields = drop.customFields; 
    
              drop.customFieldDefinitions.forEach(cfd => {
                  if(dropCustomFields[cfd.key] !== undefined && dropCustomFields[cfd.key] !== "" && dropCustomFields[cfd.key] !== null){
                    let value = dropCustomFields[cfd.key];
                    let label = cfd.label;
                    if(cfd.config.type === 'select'){
                        value = cfd.config.choices.find(c => c.key === dropCustomFields[cfd.key])?.label ?? '-';
                    }else if (cfd.config.type === 'file'){
                      let data: any = dropCustomFields[cfd.key];
                      label = data.name;
                      value = data.url;
                    }else if (cfd.config.type === 'boolean' && dropCustomFields[cfd.key] === true){
                      let data: any = dropCustomFields[cfd.key];
                      value = data;
                    }
                    
                    customFields.push({
                      key: cfd.key,
                      label: label,
                      value: value,
                      type: cfd.config.type
                    })
    
                  }
              });
              
              state.customFields = customFields;
    
            }

            let addressDetail = "";
            if (order.type === 'PickupDelivery' && 'addressDetail' in drop) {
              addressDetail = drop.addressDetail;
            } else {
              let block = 'buildingBlock' in drop && drop.buildingBlock ? `${t('Block' , language)} ${drop.buildingBlock}, ` : '';
              let floor = 'floor' in drop && drop.floor ? `${t('Floor' , language)} ${drop.floor}, `  : '';
              let room = 'room' in drop && drop.room ? `${t('Room' , language)} ${drop.room}`  : '';
              addressDetail = block + floor + room;
            }

            return {
              // As with pickup above, the raw dropoff/delivery object isn't
              // guaranteed to carry an `id` outside Ondemand's `dropoffs[]`
              // (e.g. PickupDelivery's singular `delivery`) — fall back to a
              // stable, index-derived id so lookups by id still resolve.
              id: drop.id || `dropoff-${index}`,
              lat: drop.coordinates[1],
              lng: drop.coordinates[0],
              address: drop.address,
              addressDetail: addressDetail,
              customFields: customFields && customFields.length > 0 ? customFields : null,
              deliveryDate: drop.deliveryDate,
              proofs: drop.proofs,
              customerDescription: 'customerDescription' in drop ? drop.customerDescription : '',
              proofNote: drop.podNote || ('driverDescription' in drop ? drop.driverDescription : ''),
              rawCustomFields: drop.customFields,
              rawCustomFieldDefinitions: drop.customFieldDefinitions,
              status: drop.status,
              contactName: 'fullName' in drop ? drop.fullName : drop.fullname,
              contactPhone: drop.phone,
            }

          });



        }

        

      })
      .addCase(fetchTrackOrder.rejected, (state, action) => {
        state.orderFetchStatus = "failed";
        state.error = action.payload as ApiError;

      });
  },
});

// Action creators are generated for each case reducer function
export const {
  clearOrder,
  setOrderType,
  setOrderStatus,
  setOrderDriver,
  setOrderId,
  setOrderCode,
  setOrderEvents,
  addOrderEvent,
  setEventNeedUpdate,
  setRealETA,
  setStage,
  setOrderPickup,
  setOrderDropoffs,
  setRemainingQueuePosition,
  setIsLastDropOff
} = orderSlice.actions;

export default orderSlice.reducer;
